/**
 * SessionManager — Singleton for session restore lifecycle
 *
 * Provides abort semantics for in-flight session restore requests.
 * The authOrchestrator calls cancelRestore() to prevent a late-arriving
 * verifyToken response from overriding a logout that was initiated
 * while the request was in flight.
 *
 * This replaces the anti-pattern of using window globals for cancellation.
 *
 * Pattern: Plain JS class singleton (matches socketManager.js, encryption.js,
 * errorHandler.js). See useSessionRestore.ts header for babel-eslint constraints.
 */

class SessionManager {
  constructor() {
    this.abortController = null;
  }

  /**
   * Start a new restore attempt. Cancels any previous in-flight restore.
   * @returns {AbortSignal} to pass to the verifyToken fetch/axios call
   */
  startRestore() {
    this.abortController?.abort();
    this.abortController = new AbortController();
    return this.abortController.signal;
  }

  /**
   * Cancel the current in-flight restore request (if any).
   * Called by authOrchestrator before logout cleanup begins.
   */
  cancelRestore() {
    this.abortController?.abort();
    this.abortController = null;
  }

  /**
   * Check if a restore is currently in flight.
   */
  isRestoreInFlight() {
    return (
      this.abortController !== null &&
      !this.abortController.signal.aborted
    );
  }
}

export const sessionManager = new SessionManager();
